import React, { useState, useEffect } from 'react';
import { adminAPI } from '../services/api';
import dataCache from '../services/dataCache';
import {
  Settings,
  Database,
  Edit2,
  Eye,
  EyeOff,
  Save,
  X,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';

const AdminTcgConfigs = () => {
  const [tcgConfigs, setTcgConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTcg, setEditingTcg] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const fetchTcgConfigs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getTcgConfigs();
      setTcgConfigs(response.data.data.tcgConfigs || []);
    } catch (error) {
      console.error('Error fetching TCG configs:', error);
      toast.error('Failed to fetch TCG configurations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTcgConfigs();
  }, []);

  const handleToggleActive = async (tcg) => {
    try {
      setSavingId(tcg.id);
      await adminAPI.updateTcgConfig(tcg.id, {
        isActive: !tcg.isActive
      });

      setTcgConfigs(prev => prev.map(t =>
        t.id === tcg.id ? { ...t, isActive: !t.isActive } : t
      ));

      // Clear frontend cache so Home page shows updated TCG list
      dataCache.clearCache();

      toast.success(`${tcg.displayName} ${!tcg.isActive ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error('Error toggling TCG status:', error);
      toast.error(error.response?.data?.message || 'Failed to update TCG status');
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveEdit = async (tcgId, updates) => {
    try {
      setSavingId(tcgId);
      const response = await adminAPI.updateTcgConfig(tcgId, updates);

      setTcgConfigs(prev => prev.map(t =>
        t.id === tcgId ? response.data.data.tcgConfig : t
      ));

      // Clear frontend cache so Home page shows updated TCG info
      dataCache.clearCache();

      setEditingTcg(null);
      toast.success('TCG configuration updated successfully');
    } catch (error) {
      console.error('Error updating TCG config:', error);
      toast.error(error.response?.data?.message || 'Failed to update TCG configuration');
    } finally {
      setSavingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading && tcgConfigs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Settings className="w-8 h-8 mr-3 text-primary-600" />
              TCG Configuration
            </h1>
            <p className="text-gray-600 mt-2">
              Enable or disable TCGs and manage their display settings
            </p>
          </div>
          <button
            onClick={fetchTcgConfigs}
            className="btn btn-secondary btn-md"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card p-4">
          <div className="flex items-center">
            <Database className="w-6 h-6 text-primary-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Total TCGs</p>
              <p className="text-xl font-bold">{tcgConfigs.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <Eye className="w-6 h-6 text-green-600 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Active TCGs</p>
              <p className="text-xl font-bold">
                {tcgConfigs.filter(t => t.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center">
            <EyeOff className="w-6 h-6 text-gray-400 mr-2" />
            <div>
              <p className="text-sm text-gray-600">Disabled TCGs</p>
              <p className="text-xl font-bold">
                {tcgConfigs.filter(t => !t.isActive).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* TCG Configs Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">TCG Configurations</h3>
        </div>

        {tcgConfigs.length === 0 ? (
          <div className="text-center py-12">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No TCG Configurations</h3>
            <p className="text-gray-600">No TCG configurations found in the database.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TCG
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Database
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tcgConfigs.map((tcg) => (
                  <tr key={tcg.id} className={`hover:bg-gray-50 ${!tcg.isActive ? 'bg-gray-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {tcg.displayName}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {tcg.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                        {tcg.databaseName}
                      </code>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {tcg.description || 'No description'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleActive(tcg)}
                        disabled={savingId === tcg.id}
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          tcg.isActive
                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } ${savingId === tcg.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {savingId === tcg.id ? (
                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        ) : tcg.isActive ? (
                          <Eye className="w-3 h-3 mr-1" />
                        ) : (
                          <EyeOff className="w-3 h-3 mr-1" />
                        )}
                        {tcg.isActive ? 'Active' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(tcg.updatedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setEditingTcg(tcg)}
                        className="btn btn-icon-sm btn-soft-primary"
                        title="Edit TCG"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editingTcg && (
        <EditTcgModal
          tcg={editingTcg}
          onClose={() => setEditingTcg(null)}
          onSave={handleSaveEdit}
          saving={savingId === editingTcg.id}
        />
      )}
    </div>
  );
};

// Edit TCG Modal Component
const EditTcgModal = ({ tcg, onClose, onSave, saving }) => {
  const [formData, setFormData] = useState({
    displayName: tcg.displayName || '',
    description: tcg.description || '',
    logoUrl: tcg.logoUrl || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(tcg.id, {
      displayName: formData.displayName.trim(),
      description: formData.description.trim() || null,
      logoUrl: formData.logoUrl.trim() || null
    });
  };

  const isValidForm = formData.displayName.trim().length >= 2;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-medium mb-4 flex items-center">
          <Edit2 className="w-5 h-5 mr-2 text-primary-600" />
          Edit TCG Configuration
        </h3>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Name
            </label>
            <input
              type="text"
              value={tcg.name}
              disabled
              className="input-field bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Internal name cannot be changed</p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Display Name *
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({...formData, displayName: e.target.value})}
              className="input-field"
              placeholder="Pokemon TCG"
              required
              minLength={2}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field"
              placeholder="A brief description of this TCG..."
              rows="3"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo URL
            </label>
            <input
              type="text"
              value={formData.logoUrl}
              onChange={(e) => setFormData({...formData, logoUrl: e.target.value})}
              className="input-field"
              placeholder="https://example.com/logo.png"
            />
            <p className="text-xs text-gray-500 mt-1">URL to the TCG logo image</p>
            {formData.logoUrl && (
              <div className="mt-2">
                <img
                  src={formData.logoUrl}
                  alt="TCG Logo Preview"
                  className="h-16 object-contain border border-gray-200 rounded p-2 bg-gray-50"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Database Name
            </label>
            <input
              type="text"
              value={tcg.databaseName}
              disabled
              className="input-field bg-gray-100 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1">Database name cannot be changed</p>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary btn-md"
              disabled={saving}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-md"
              disabled={!isValidForm || saving}
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminTcgConfigs;
